import { addDays, setHours, setMinutes, setSeconds } from "date-fns";
import { log } from "./log";
import {
  type NetworkStatus,
  type NetworkStatusObserver,
  observeNetworkStatus,
} from "./network";
import type {
  SyncCheckResult,
  SyncDataProvider,
  SyncExecution,
  SyncManagerEventListener,
  SyncManagerEventTypes,
  SyncManagerPersistence,
  SyncModule,
  SyncModuleStatus,
  SyncObserver,
  SyncOptions,
  SyncResult,
} from "./types";

export class SyncManager implements SyncDataProvider {
  networkStatus: NetworkStatus = {
    connectionType: "unknown",
    effectiveType: "unknown",
  };

  readonly modules = new Map<string, SyncModule<any>>();
  readonly moduleOptions = new Map<string, SyncOptions>();
  readonly moduleStatus = new Map<string, SyncModuleStatus>();
  readonly moduleExecutions = new Map<string, SyncExecution>();
  readonly moduleData = new Map<string, unknown>();

  readonly #networkStatusObserver: NetworkStatusObserver;
  readonly #persistence: SyncManagerPersistence;
  readonly #listeners = new Map<string, Set<SyncManagerEventListener<any>>>();

  constructor({ persistence }: { persistence: SyncManagerPersistence }) {
    this.#persistence = persistence;

    setInterval(() => this.scheduleModules(), 10000);
    this.on("network-status-changed", () => this.scheduleModules());

    this.#networkStatusObserver = observeNetworkStatus();
    this.networkStatus = this.#networkStatusObserver.status;
    this.#networkStatusObserver.on("update", ({ status }) => {
      this.networkStatus = status;
      this.#emit("network-status-changed", { status });
    });
  }

  updateNetworkStatus() {
    this.#networkStatusObserver.update();
  }

  setNetworkStatus(status: NetworkStatus) {
    this.#networkStatusObserver.setStatus(status);
  }

  async registerModule<TData>(module: SyncModule<TData>) {
    if (this.modules.has(module.id)) {
      log.warn(`Duplicate module registration: ${module.id}`);
      return;
    }

    log.verbose(`Registering module '${module.id}'`);
    this.modules.set(module.id, module);

    const [storedOptions, storedStatus, storedData] = await Promise.all([
      this.#persistence.loadModuleOptions(module.id),
      this.#persistence.loadModuleStatus(module.id),
      this.#persistence.loadModuleData(module.id),
    ]);

    await this.setModuleOptions(
      module.id,
      { ...module.defaultOptions, ...storedOptions },
      true,
    );

    await this.#setModuleStatus(module.id, storedStatus ?? {}, true);

    if (storedData !== undefined) {
      await this.#setModuleData(module.id, storedData, true);
    }

    this.#emit("module-registered", { module });
  }

  async scheduleModules() {
    const now = Date.now();
    await Promise.all(
      [...this.modules.keys()].map((moduleId) =>
        this.#syncModule(moduleId, now),
      ),
    );
  }

  async #syncModule(moduleId: string, now: number = Date.now()) {
    const options = this.moduleOptions.get(moduleId);
    const status = this.moduleStatus.get(moduleId) ?? {};
    if (!options) throw new Error(`Module not found: ${moduleId}`);

    if (this.moduleExecutions.has(moduleId)) {
      log.info(`[${moduleId}] Execution is running, not synching now.`);
      return;
    }

    const syncResult = this.#checkModuleSync(moduleId, options, status, now);

    await this.#updateModuleStatus(moduleId, (current) => ({
      ...current,
      lastSyncCheck: syncResult,
    }));

    if (syncResult.result === "sync") {
      this.syncModuleNow(moduleId);
    }
  }

  #checkModuleSync(
    moduleId: string,
    options: SyncOptions,
    status: SyncModuleStatus,
    now: number,
  ): SyncCheckResult {
    if (options.schedule.type === "manual") {
      log.verbose(
        `[${moduleId}] Module is set to manual sync, not synching now.`,
      );
      return { checkedAt: now, result: "manual-schedule" };
    }

    if (status.nextScheduledAt && status.nextScheduledAt > now) {
      log.verbose(
        `[${moduleId}] Module is scheduled to be synchronized at ${new Date(status.nextScheduledAt).toISOString()}, not synching now.`,
      );
      return { checkedAt: now, result: "waiting-for-schedule" };
    }

    const requiredNetworkStatus = options.requiredNetworkStatus ?? [
      "cellular",
      "wifi",
    ];

    if (!requiredNetworkStatus.includes(this.networkStatus.effectiveType)) {
      log.info(
        `[${moduleId}] Module requires network status to be ${requiredNetworkStatus?.join(" or ")}, not synching now.`,
      );
      return { checkedAt: now, result: "waiting-for-network-status" };
    }

    const module = this.modules.get(moduleId);

    if (module?.dependsOn) {
      log.verbose(
        `[${moduleId}] Checking module dependencies: ${module.dependsOn.join(", ")}…`,
      );

      const missingDependencies = module.dependsOn.filter((dependsOn) => {
        const execution = this.moduleExecutions.get(dependsOn);
        if (execution) {
          log.verbose(
            `[${moduleId}] Module dependency is currently running: ${dependsOn}`,
          );
          return true;
        }

        const status = this.moduleStatus.get(dependsOn);
        if (status?.lastSuccessfulResult) {
          log.verbose(
            `[${moduleId}] Module dependency had a successful run: ${dependsOn}`,
          );
          return false;
        }

        log.verbose(
          `[${moduleId}] Module dependency did not run successfully yet: ${dependsOn}`,
        );
        return true;
      });

      if (missingDependencies.length) {
        log.info(
          `[${moduleId}] Module dependends on job(s) ${missingDependencies.join(", ")}, not synching now.`,
        );
        return { checkedAt: now, result: "waiting-for-dependencies" };
      }
    }

    log.verbose(`[${moduleId}] Module needs to be synchronized, starting sync`);
    return { checkedAt: now, result: "sync" };
  }

  async syncModuleNow(moduleId: string) {
    const module = this.modules.get(moduleId);
    if (!module) throw new Error(`Module not found: ${moduleId}`);

    log.info(`[${moduleId}] Starting synchronization`);
    const startedAt = Date.now();
    try {
      this.#setModuleExecution(moduleId, { startedAt, canceled: false });

      await this.#updateModuleStatus(moduleId, (current) => ({
        ...current,
        currentStatus: { status: "pending", startedAt },
      }));

      const observer = this.#createSyncObserver(moduleId);
      const data = await module.fetchData({ dataProvider: this, observer });
      await this.#setModuleData(moduleId, data);

      log.info(
        `[${moduleId}] Synchronization completed after ${Date.now() - startedAt} ms`,
      );

      const execution = this.moduleExecutions.get(moduleId);
      const canceled = execution?.canceled ?? false;

      const result: SyncResult = {
        status: canceled ? "canceled" : "success",
        startedAt,
        finishedAt: Date.now(),
        totalWork: execution?.work,
        completedWork: execution?.completed,
      };

      await this.#updateModuleStatus(moduleId, (current) => ({
        ...current,
        lastFinishedResult: result,
        ...(result.status === "success"
          ? { lastSuccessfulResult: result }
          : {}),
        nextScheduledAt: this.#getNextScheduledAt(moduleId, result),
      }));
    } catch (error: any) {
      log.warn(
        `[${moduleId}] Synchronization failed after ${Date.now() - startedAt} ms: ${error.message}`,
      );

      const execution = this.moduleExecutions.get(moduleId);

      const result: SyncResult = {
        status: "error",
        startedAt,
        finishedAt: Date.now(),
        error,
        totalWork: execution?.work,
        completedWork: execution?.completed,
      };

      await this.#updateModuleStatus(moduleId, (current) => ({
        ...current,
        lastFinishedResult: result,
        nextScheduledAt: this.#getNextScheduledAt(moduleId, result),
      }));
    } finally {
      await this.#setModuleExecution(moduleId, undefined);
    }
  }

  cancelExecution(moduleId: string) {
    this.#updateModuleExecution(moduleId, (current) => ({
      ...current,
      canceled: true,
    }));
  }

  #createSyncObserver(moduleId: string): SyncObserver {
    const isCanceled = (): boolean => {
      return this.moduleExecutions.get(moduleId)?.canceled ?? false;
    };

    const setWork = (work: number) => {
      this.#updateModuleExecution(moduleId, (current) => ({
        ...current,
        work,
        completed: current.completed ?? 0,
      }));
    };

    const addWork = (work: number) => {
      this.#updateModuleExecution(moduleId, (current) => ({
        ...current,
        work: (current.work ?? 0) + work,
        completed: current.completed ?? 0,
      }));
    };

    const workCompleted = (work: number = 1) => {
      this.#updateModuleExecution(moduleId, (current) => ({
        ...current,
        completed: (current.completed ?? 0) + work,
      }));
    };

    return {
      setWork,
      addWork,
      workCompleted,
      get canceled() {
        return isCanceled();
      },
    };
  }

  #getNextScheduledAt(
    moduleId: string,
    lastFinishedResult?: SyncResult,
  ): number | undefined {
    const options = this.moduleOptions.get(moduleId);
    if (!options) return undefined;

    switch (options.schedule.type) {
      case "periodic":
        return (
          (lastFinishedResult?.startedAt ?? Date.now()) +
          options.schedule.interval
        );
      case "daily": {
        let reference = lastFinishedResult?.startedAt
          ? new Date(lastFinishedResult?.startedAt)
          : new Date();

        if (
          reference.getHours() > options.schedule.hour ||
          (reference.getHours() === options.schedule.hour &&
            reference.getMinutes() > options.schedule.minute)
        ) {
          reference = addDays(reference, 1);
        }

        return setSeconds(
          setMinutes(
            setHours(reference, options.schedule.hour),
            options.schedule.minute,
          ),
          0,
        ).getTime();
      }
      default:
        return undefined;
    }
  }

  async setModuleOptions(
    moduleId: string,
    options: SyncOptions,
    skipPersistence: boolean = false,
  ) {
    this.moduleOptions.set(moduleId, options);
    this.#emit("module-options-updated", { moduleId, options });
    if (!skipPersistence) {
      await this.#persistence.saveModuleOptions(moduleId, options);

      await this.#updateModuleStatus(moduleId, (status) => ({
        ...status,
        nextScheduledAt: this.#getNextScheduledAt(
          moduleId,
          status?.lastFinishedResult,
        ),
      }));
    }
  }

  async #setModuleData(
    moduleId: string,
    data: unknown,
    skipPersistence: boolean = false,
  ) {
    this.moduleData.set(moduleId, data);
    this.#emit("module-data-updated", { moduleId, data });
    if (!skipPersistence) {
      await this.#persistence.saveModuleData(moduleId, data);
    }
  }

  async #setModuleStatus(
    moduleId: string,
    status: SyncModuleStatus,
    skipPersistence: boolean = false,
  ) {
    this.moduleStatus.set(moduleId, status);
    this.#emit("module-status-updated", { moduleId, status });
    if (!skipPersistence) {
      await this.#persistence.saveModuleStatus(moduleId, status);
    }
  }

  async #updateModuleStatus(
    moduleId: string,
    updater: (status: SyncModuleStatus) => SyncModuleStatus,
  ) {
    const current = this.moduleStatus.get(moduleId);
    if (!current) return;
    const status = updater(current);
    return this.#setModuleStatus(moduleId, status);
  }

  #setModuleExecution(moduleId: string, execution?: SyncExecution) {
    if (execution) {
      this.moduleExecutions.set(moduleId, execution);
      this.#emit("module-execution-updated", { moduleId, execution });
    } else {
      this.moduleExecutions.delete(moduleId);
      this.#emit("module-execution-finished", { moduleId });
    }
  }

  #updateModuleExecution(
    moduleId: string,
    updater: (current: SyncExecution) => SyncExecution,
  ) {
    const current = this.moduleExecutions.get(moduleId);
    if (!current) return;
    const next = updater(current);
    return this.#setModuleExecution(moduleId, next);
  }

  getModuleData<TData>(moduleId: string): TData | undefined {
    return this.moduleData.get(moduleId) as TData | undefined;
  }

  async cancelAllExecutions() {
    await Promise.all(
      this.moduleExecutions
        .keys()
        .map((moduleId) => this.cancelExecution(moduleId)),
    );
  }

  on<T extends keyof SyncManagerEventTypes>(
    type: T,
    listener: SyncManagerEventListener<T>,
  ) {
    let listeners = this.#listeners.get(type);
    if (!listeners) {
      listeners = new Set<SyncManagerEventListener<any>>();
      this.#listeners.set(type, listeners);
    }
    listeners.add(listener);
  }

  off<T extends keyof SyncManagerEventTypes>(
    type: T,
    listener: SyncManagerEventListener<T>,
  ) {
    const listeners = this.#listeners.get(type);
    if (listeners) listeners.delete(listener);
  }

  #emit<T extends keyof SyncManagerEventTypes>(
    type: T,
    event: SyncManagerEventTypes[T],
  ) {
    const listeners = this.#listeners.get(type);
    if (listeners) {
      for (const listener of listeners) {
        listener(event);
      }
    }
  }
}
